import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';
import { WritableDraft } from 'immer/dist/types/types-external';
import {
  cloneDeep,
  isArray,
  isPlainObject,
  kebabCase,
  map,
  mapKeys,
  mapValues,
  remove,
  snakeCase,
  unionBy,
} from 'lodash/fp';

import { AccountType } from 'dashboard/models/account-type';
import { Case, CaseActionWorkflow, CaseDataManager, CaseObject } from 'dashboard/models/case';
import { CaseAssignmentPolicy } from 'dashboard/models/case-assignment-policy';
import { CaseComment } from 'dashboard/models/case-comment';
import { CaseMessage } from 'dashboard/models/case-message';
import { CaseQueryResult } from 'dashboard/models/case-query-result';
import { CaseTemplate } from 'dashboard/models/case-template';
import { Device } from 'dashboard/models/device';
import { Document } from 'dashboard/models/document';
import { GraphSnapshot } from 'dashboard/models/graph-snapshot';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquirySession } from 'dashboard/models/inquiry-session';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryShowViewInquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Report } from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { TransactionStatus } from 'dashboard/models/transaction-status';
import { TransactionType } from 'dashboard/models/transaction-type';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import { WorkflowRunStepAction } from 'dashboard/models/workflow-step';

export type CaseRecordMap = Record<
  string,
  { case: Case | undefined; caseDetails: Partial<CaseDetails> }
>;

export interface CaseDetails {
  accountTypes: AccountType[];
  actionWorkflows: CaseActionWorkflow[];
  assignee: User | undefined;
  caseComments: CaseComment[];
  caseDataManager: CaseDataManager | undefined;
  caseMessages: CaseMessage[];
  caseQueryResults: CaseQueryResult[];
  caseTemplate: CaseTemplate | undefined;
  commentWorkflows: Workflow[];
  commentWorkflowStepActions: WorkflowRunStepAction[];
  commentWorkflowRuns: WorkflowRun[];
  creator: User | WorkflowRun | undefined;
  documents: Document[];
  graphSnapshots: GraphSnapshot[];
  inquiriesDetails: {
    inquiries: Inquiry[];
    devices: Device[];
    documents: Document[];
    inquiryTemplateVersions: InquiryShowViewInquiryTemplateVersion[];
    inquiryVerifications: Verification[];
    listItems: ListItem[];
    listMatches: ListMatch[];
    lists: List[];
    sessions: InquirySession[];
    tags: Tag[];
    templates: (Template | InquiryTemplate)[];
  };
  reportsDetails: {
    reports: Report[];
    reportTemplates: ReportTemplate[];
    tags: Tag[];
  };
  objects: CaseObject[];
  resolver: User | WorkflowRun | undefined;
  caseTags: Tag[];
  tags: Tag[];
  transactionStatuses: TransactionStatus[];
  transactionTypes: TransactionType[];
  updater: CaseAssignmentPolicy | User | WorkflowRun | undefined;
  verifications: Verification[];
}

export interface CaseState {
  case: CaseRecordMap;
  caseToken?: string;
}

export const initialState: CaseState = {
  case: {},
  caseToken: undefined,
};

const initialCaseDetailsState: CaseDetails = {
  accountTypes: [],
  actionWorkflows: [],
  assignee: undefined,
  caseComments: [],
  caseDataManager: undefined,
  caseMessages: [],
  caseQueryResults: [],
  caseTemplate: undefined,
  commentWorkflows: [],
  commentWorkflowStepActions: [],
  commentWorkflowRuns: [],
  creator: undefined,
  documents: [],
  graphSnapshots: [],
  inquiriesDetails: {
    inquiries: [],
    devices: [],
    documents: [],
    inquiryTemplateVersions: [],
    inquiryVerifications: [],
    listItems: [],
    listMatches: [],
    lists: [],
    sessions: [],
    tags: [],
    templates: [],
  },
  reportsDetails: {
    reports: [],
    reportTemplates: [],
    tags: [],
  },
  objects: [],
  resolver: undefined,
  caseTags: [],
  tags: [],
  transactionStatuses: [],
  transactionTypes: [],
  updater: undefined,
  verifications: [],
};

const initialStateCaseRecord = {
  case: undefined,
  caseDetails: initialCaseDetailsState,
};

interface SetCasePayload {
  key: string;
  case: Case;
  caseDetails?: Partial<CaseDetails>;
}

interface SetCaseTokenPayload {
  key: string;
}

interface SetCaseCommentPayload {
  key: string;
  comment: CaseComment;
}

interface RemoveCaseCommentPayload {
  key: string;
  id: string;
}

interface SetCaseDataManagerPayload {
  key: string;
  caseDataManager: CaseDataManager;
}

interface SetCaseObjectsPayload {
  key: string;
  objects: CaseObject[];
}

interface SetCaseMessagePayload {
  key: string;
  caseMessage: CaseMessage;
}

interface SetCaseQueryResultsPayload {
  key: string;
  caseQueryResults: CaseQueryResult[];
}

interface SetCaseQueryResultPayload {
  key: string;
  caseQueryResult: CaseQueryResult;
}

interface RemoveCaseMessagePayload {
  key: string;
  id: string;
}

interface SetInquiriesDetailsPayload {
  key: string;
  devices: Device[];
  documents: Document[];
  inquiries: Inquiry[];
  inquiryTemplateVersions: InquiryShowViewInquiryTemplateVersion[];
  inquiryVerifications: Verification[];
  listItems: ListItem[];
  listMatches: ListMatch[];
  lists: List[];
  sessions: InquirySession[];
  tags: Tag[];
  templates: (Template | InquiryTemplate)[];
}

interface SetReportsDetailsPayload {
  key: string;
  reports: Report[];
  reportTemplates: ReportTemplate[];
  tags: Tag[];
}

interface DeeplyNestedObject {
  [key: string]: Value;
}

type Value = string | number | boolean | DeeplyNestedObject | Value[];

function mapKeysDeep(object: Value, fn: (key: string) => string): Value {
  if (isArray(object)) {
    return map((value: Value) => mapKeysDeep(value, fn))(object);
  } else if (isPlainObject(object) && typeof object === 'object') {
    const objWithTransformedKeys = mapKeys((key: string) => fn(key))(object);
    return mapValues((value: Value) => mapKeysDeep(value, fn))(objWithTransformedKeys);
  } else {
    return object;
  }
}

function mapObjectKeys(
  caseDataManager: DeeplyNestedObject,
  fn: (key: string) => string,
): DeeplyNestedObject {
  return mapValues((value: Value) => mapKeysDeep(value, fn))(
    mapKeys((key: string) => fn(key))(caseDataManager),
  );
}

// We use this method to generate ensure that we have an initial state for a given key (case token).
// We have to do this because case request responses come back at variable times (e.g. we fire off
// requests for GET /cases/case_123, GET /cases/case_123/inquiry-objects, and GET /cases/case_123/report_objects)
// all at the same time, but we do not know which response will come back first, and so each of these
// handlers must be able to create the initial state for them to write into.
const generateInitialStateCaseRecord = (state: WritableDraft<CaseState>, key: string) => {
  return {
    ...castDraft(initialStateCaseRecord),
    ...state.case[key],
  };
};

const { reducer, actions } = createSlice({
  name: 'case',
  initialState,
  reducers: {
    clearCase: () => {
      return initialState;
    },
    setCaseToken: (state, action: PayloadAction<SetCaseTokenPayload>) => {
      state.caseToken = action.payload.key;
      state.case[action.payload.key] = generateInitialStateCaseRecord(state, action.payload.key);
    },
    setCase: (state, action: PayloadAction<SetCasePayload>) => {
      state.case[action.payload.key] = generateInitialStateCaseRecord(state, action.payload.key);

      state.case[action.payload.key] = {
        ...state.case[action.payload.key],
        case: castDraft(action.payload.case),
      };

      if (action.payload.caseDetails) {
        state.case[action.payload.key].caseDetails = {
          ...state.case[action.payload.key].caseDetails,
          ...castDraft(action.payload.caseDetails),
        };
      }
    },
    setCaseComment: (state, action: PayloadAction<SetCaseCommentPayload>) => {
      state.case[action.payload.key] = generateInitialStateCaseRecord(state, action.payload.key);

      state.case[action.payload.key].caseDetails.caseComments = unionBy(
        'id',
        [action.payload.comment],
        state.case[action.payload.key].caseDetails.caseComments,
      );
    },
    removeCaseComment: (state, action: PayloadAction<RemoveCaseCommentPayload>) => {
      state.case[action.payload.key] = generateInitialStateCaseRecord(state, action.payload.key);

      state.case[action.payload.key].caseDetails.caseComments = remove(
        (comment) => comment.id === action.payload.id,
        state.case[action.payload.key].caseDetails.caseComments || [],
      );
    },
    setCaseDataManager: (state, action: PayloadAction<SetCaseDataManagerPayload>) => {
      state.case[action.payload.key] = generateInitialStateCaseRecord(state, action.payload.key);

      const caseDataManager = cloneDeep(action.payload.caseDataManager);
      /**
       * NOTE: While we migrate away from forced api key inflection, we ensure backwards
       * compatibility by making sure both snake_case and kebab-case fields are available.
       * We can delete this code once two conditions are satisfied:
       * 1. Our API returns the original key inflection of fields.
       * 2. All configuration is updated to use original key inflection of the respective field.
       */

      // iterate through all fields and create a dasherized copy of the field
      const kebabFields = mapObjectKeys(caseDataManager.case?.fields, kebabCase);
      // iterate through all fields and create a snake_cased copy of the field
      const snakeFields = mapObjectKeys(caseDataManager.case?.fields, snakeCase);

      // NOTE(brandon): caseDataManager.case should never be null, but strict mode forces us to be careful here.
      if (caseDataManager.case == null) {
        caseDataManager.case = {
          fields: {},
        };
      }

      // merge the dasherized fields with original fields
      caseDataManager.case.fields = {
        ...caseDataManager.case.fields,
        ...kebabFields,
        ...snakeFields,
      };

      // For backwards compatibility during migration, we add caseDataManager as a nested object of itself because old code references it as a variable
      caseDataManager.caseDataManager = cloneDeep(caseDataManager);

      // These keys will eventually be variables in the future, schemas will be autocompleting these keys moving forward
      caseDataManager.current_case = mapObjectKeys(caseDataManager.case || {}, snakeCase);
      caseDataManager.current_user = mapObjectKeys(
        caseDataManager['current-user'] || {},
        snakeCase,
      );

      state.case[action.payload.key].caseDetails.caseDataManager = caseDataManager;
    },
    setCaseObjects: (state, action: PayloadAction<SetCaseObjectsPayload>) => {
      state.case[action.payload.key] = generateInitialStateCaseRecord(state, action.payload.key);

      state.case[action.payload.key].caseDetails.objects = castDraft(action.payload.objects);
    },
    setCaseMessage: (state, action: PayloadAction<SetCaseMessagePayload>) => {
      state.case[action.payload.key] = generateInitialStateCaseRecord(state, action.payload.key);

      state.case[action.payload.key].caseDetails.caseMessages = unionBy(
        'id',
        [action.payload.caseMessage],
        state.case[action.payload.key].caseDetails.caseMessages,
      );
    },
    setCaseQueryResults: (state, action: PayloadAction<SetCaseQueryResultsPayload>) => {
      state.case[action.payload.key] = generateInitialStateCaseRecord(state, action.payload.key);

      state.case[action.payload.key].caseDetails.caseQueryResults = unionBy(
        'id',
        action.payload.caseQueryResults,
        state.case[action.payload.key].caseDetails.caseQueryResults,
      );
    },
    setCaseQueryResult: (state, action: PayloadAction<SetCaseQueryResultPayload>) => {
      state.case[action.payload.key] = generateInitialStateCaseRecord(state, action.payload.key);

      state.case[action.payload.key].caseDetails.caseQueryResults = unionBy(
        'id',
        [action.payload.caseQueryResult],
        state.case[action.payload.key].caseDetails.caseQueryResults,
      );
    },
    removeCaseMessage: (state, action: PayloadAction<RemoveCaseMessagePayload>) => {
      state.case[action.payload.key] = generateInitialStateCaseRecord(state, action.payload.key);

      state.case[action.payload.key].caseDetails.caseMessages = remove(
        (message) => message.id === action.payload.id,
        state.case[action.payload.key].caseDetails.caseMessages || [],
      );
    },
    setInquiriesDetails: (state, action: PayloadAction<SetInquiriesDetailsPayload>) => {
      const { key, ...payload } = action.payload;

      state.case[key] = generateInitialStateCaseRecord(state, key);

      // NOTE: tags is expected to include tags both from the case and from the inquiries
      state.case[key].caseDetails.tags = unionBy(
        'id',
        payload.tags,
        state.case[key].caseDetails.tags,
      );
      state.case[key].caseDetails.inquiriesDetails = {
        ...state.case[key].caseDetails.inquiriesDetails,
        ...castDraft(payload),
      };
    },
    setReportsDetails: (state, action: PayloadAction<SetReportsDetailsPayload>) => {
      const { key, ...payload } = action.payload;

      state.case[key] = generateInitialStateCaseRecord(state, key);

      state.case[key].caseDetails.reportsDetails = {
        ...state.case[key].caseDetails.reportsDetails,
        ...payload,
      };
    },
  },
});

export const {
  clearCase,
  setCase,
  setCaseToken,
  setCaseComment,
  setCaseQueryResult,
  setCaseQueryResults,
  removeCaseComment,
  setCaseDataManager,
  setCaseObjects,
  setCaseMessage,
  removeCaseMessage,
  setInquiriesDetails,
  setReportsDetails,
} = actions;

export const caseReducer = reducer;
